/**
 * Admin API client. Uses REACT_APP_API_URL and stores admin token in localStorage.
 * Converts between API snake_case and frontend camelCase.
 */

const STORAGE_KEY = "viewstruct_admin_token";

function getBaseUrl() {
  const url = process.env.REACT_APP_API_URL;
  if (!url) {
    throw new Error("REACT_APP_API_URL is not set");
  }
  return url.replace(/\/$/, "");
}

export function getToken() {
  return localStorage.getItem(STORAGE_KEY);
}

export function setToken(token) {
  localStorage.setItem(STORAGE_KEY, token);
}

export function clearToken() {
  localStorage.removeItem(STORAGE_KEY);
}

class ApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

function formatApiErrorMessage(body, statusText) {
  if (!body) return statusText;
  const detail = body.detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((d) => (typeof d === "object" && d?.msg != null ? d.msg : String(d)))
      .filter(Boolean);
    if (messages.length) return messages.join(". ");
  }
  if (typeof detail === "string") return detail;
  if (body.message) return body.message;
  return statusText;
}

async function request(path, options = {}, isRetryAfterRefresh = false) {
  const base = getBaseUrl();
  const token = getToken();
  const headers = {
    "Content-Type": "application/json",
    ...options.headers,
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${base}${path}`, {
    ...options,
    headers,
    credentials: "include",
  });
  const text = await res.text();
  let body = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch (_) {
    body = { detail: text };
  }
  if (!res.ok) {
    if (res.status === 401 && !isRetryAfterRefresh) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        return request(path, options, true);
      }
      clearToken();
    } else if (res.status === 401) {
      clearToken();
    }
    const message = formatApiErrorMessage(body, res.statusText);
    throw new ApiError(message, res.status, body);
  }
  return body;
}

/** Refresh access token using HTTP-only refresh cookie. Returns true if new token was set. */
async function refreshAccessToken() {
  const base = getBaseUrl();
  try {
    const res = await fetch(`${base}/admin/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    const text = await res.text();
    let data = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch (_) {
      return false;
    }
    if (!res.ok || !data?.access_token) return false;
    setToken(data.access_token);
    return true;
  } catch (_) {
    return false;
  }
}

// —— Auth ——
export async function login(username, password) {
  const data = await request("/admin/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });
  return data; // { access_token, token_type }
}

/** Call backend logout to clear refresh token cookie, then clear local token. */
export async function logout() {
  const base = getBaseUrl();
  try {
    await fetch(`${base}/admin/logout`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
  } catch (_) {
    // ignore network errors; still clear local token
  }
  clearToken();
}

// —— Organizations (API snake_case → frontend camelCase) ——
function toOrg(org) {
  if (!org) return org;
  return {
    id: org.id,
    name: org.name,
    address: org.address ?? "",
    phone: org.phone ?? "",
    email: org.email ?? "",
    registrationDate: org.registration_date || null,
  };
}

export async function getOrganizations() {
  const list = await request("/admin/organizations");
  return Array.isArray(list) ? list.map(toOrg) : [];
}

export async function getOrganization(id) {
  const org = await request(`/admin/organizations/${id}`);
  return toOrg(org);
}

export async function createOrganization(data) {
  const payload = {
    name: data.name,
    address: data.address || null,
    phone: data.phone || null,
    email: data.email || null,
  };
  const org = await request("/admin/organizations", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return toOrg(org);
}

export async function updateOrganization(id, data) {
  const payload = {
    name: data.name,
    address: data.address ?? undefined,
    phone: data.phone ?? undefined,
    email: data.email ?? undefined,
  };
  const org = await request(`/admin/organizations/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
  return toOrg(org);
}

export async function deleteOrganization(id) {
  return request(`/admin/organizations/${id}`, { method: "DELETE" });
}

// —— Roles ——
function toRole(role) {
  if (!role) return role;
  return {
    id: role.id,
    name: role.name,
    description: role.description ?? "",
    department: role.department ?? "",
    level: role.level ?? "",
    createdAt: role.created_at || null,
  };
}

export async function getRoles() {
  const list = await request("/admin/roles");
  return Array.isArray(list) ? list.map(toRole) : [];
}

export async function getRole(id) {
  const role = await request(`/admin/roles/${id}`);
  return toRole(role);
}

export async function createRole(data) {
  const payload = {
    name: data.name,
    description: data.description ?? null,
    department: data.department ?? null,
    level: data.level ?? null,
  };
  const role = await request("/admin/roles", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return toRole(role);
}

export async function updateRole(id, data) {
  const payload = {
    description: data.description ?? undefined,
    department: data.department ?? undefined,
    level: data.level ?? undefined,
  };
  const role = await request(`/admin/roles/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
  return toRole(role);
}

export async function deleteRole(id) {
  await request(`/admin/roles/${id}`, { method: "DELETE" });
}

// —— RBAC: endpoints and role-endpoints ——
function toEndpoint(ep) {
  if (!ep) return ep;
  return {
    id: ep.id,
    code: ep.code,
    method: ep.method,
    pathPattern: ep.path_pattern,
    name: ep.name ?? "",
    isPublic: ep.is_public ?? false,
  };
}

export async function getEndpoints() {
  const list = await request("/admin/endpoints");
  return Array.isArray(list) ? list.map(toEndpoint) : [];
}

export async function getRoleEndpoints(roleId) {
  const data = await request(`/admin/roles/${roleId}/endpoints`);
  return data?.endpoint_ids ?? [];
}

export async function updateRoleEndpoints(roleId, endpointIds) {
  await request(`/admin/roles/${roleId}/endpoints`, {
    method: "PUT",
    body: JSON.stringify({ endpoint_ids: endpointIds }),
  });
  return { roleId, endpointIds };
}

// —— Users (employees): list-users returns { users, count } ——
function toEmployee(user) {
  if (!user) return user;
  return {
    id: user.id,
    organizationId: user.organization_id,
    roleId: user.role_id ?? null,
    firstName: user.first_name ?? "",
    lastName: user.last_name ?? "",
    name: user.name ?? "",
    position: user.position ?? "",
    email: user.email ?? "",
    phone: user.phone ?? "",
    hireDate: user.hire_date || null,
  };
}

export async function listUsers(params = {}) {
  const q = new URLSearchParams();
  if (params.skip != null) q.set("skip", params.skip);
  if (params.limit != null) q.set("limit", params.limit);
  if (params.organization_id != null)
    q.set("organization_id", params.organization_id);
  const query = q.toString();
  const data = await request(
    `/admin/list-users${query ? `?${query}` : ""}`
  );
  const users = data?.users ?? [];
  return {
    users: users.map(toEmployee),
    count: data?.count ?? users.length,
  };
}

export async function getUser(id) {
  const user = await request(`/admin/users/${id}`);
  return toEmployee(user);
}

export async function createUser(data) {
  const payload = {
    email: data.email,
    name: data.name || `${(data.firstName || "").trim()} ${(data.lastName || "").trim()}`.trim() || "User",
    password: data.password,
    organization_id: data.organizationId,
    role_id: data.roleId || null,
    first_name: data.firstName || null,
    last_name: data.lastName || null,
    position: data.position || null,
    phone: data.phone || null,
    hire_date: data.hireDate || null,
  };
  const user = await request("/admin/users", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return toEmployee(user);
}

export async function updateUser(id, data) {
  const payload = {};
  if (data.email !== undefined) payload.email = data.email;
  if (data.name !== undefined) payload.name = data.name;
  if (data.password !== undefined && data.password !== "")
    payload.password = data.password;
  if (data.organizationId !== undefined)
    payload.organization_id = data.organizationId;
  if (data.roleId !== undefined) payload.role_id = data.roleId;
  if (data.firstName !== undefined) payload.first_name = data.firstName;
  if (data.lastName !== undefined) payload.last_name = data.lastName;
  if (data.position !== undefined) payload.position = data.position;
  if (data.phone !== undefined) payload.phone = data.phone;
  if (data.hireDate !== undefined) payload.hire_date = data.hireDate;
  const user = await request(`/admin/users/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
  return toEmployee(user);
}

export async function deleteUser(id) {
  return request(`/admin/users/${id}`, { method: "DELETE" });
}

export { ApiError };
